import { Owner } from '../../../models/polymer/Owner.model';
import { P2Accordion, P2TextDescription, P2TextLabel } from 'react-bootstrap-common-script';
import { TextInfo } from '../../text';
import { Occupier } from '../../../models/polymer/Occupier.model';
import { useState, useCallback } from 'react';
import { ResidentialStatus, RelationshipWithOwner, MaritalStatusCode, NationalityCode } from '../../../utils/code-converter';
import { idTypesInterface, ownerChildrenCareItems, raceDropdownItems } from '../../../utils/shared-dropdown';
import { useAppSelector } from '../../../redux/hook';
import { SexEnum } from '../../../models/enum/sex.enum';
import { Helpers } from '../../../utils/helper';

type OwnerOrOccupierReviewAccordionProps = {
  first_person?: string;
  is_first_person?: boolean;
  enableMasking?: boolean;
  showMonthlyIncome?: boolean;
} & ({ isOwner: true; person: Owner } | { isOwner: false; person: Occupier });

function OwnerText({
  is_first_person,
  first_person,
  is_related,
  relationshipWithOwner,
}: {
  is_first_person: boolean;
  first_person: string;
  is_related: boolean;
  relationshipWithOwner: string;
}) {
  return relationshipWithOwner ? (
    <div className='polymer-owner-review-accordion__fields-container'>
      <P2TextLabel bold={700} label={'Relationship'} />
      <TextInfo
        variant='column'
        title={`Is this proposed owner related to ${is_first_person ? 'the current owner(s)' : first_person}?`}
        description={is_related ? 'Yes' : 'No'}
      />
      <TextInfo
        variant='column'
        title={`How is this proposed owner related to ${is_first_person ? 'the current owner(s)' : first_person}?`}
        description={relationshipWithOwner}
      />
    </div>
  ) : null;
}

function OccupierText({ first_person, relationshipWithOwner }: { first_person: string; relationshipWithOwner: string }) {
  return relationshipWithOwner ? (
    <div className='polymer-owner-review-accordion__fields-container'>
      <P2TextLabel bold={700} label={'Relationship'} />
      <TextInfo variant='column' title={`How is this proposed occupier related to ${first_person}?`} description={relationshipWithOwner} />
    </div>
  ) : null;
}

function OwnerOrOccupierReviewAccordion({
  first_person = 'owner',
  is_first_person = false,
  showMonthlyIncome = true,
  ...props
}: OwnerOrOccupierReviewAccordionProps) {
  const [isOpen, setIsOpen] = useState(true);
  const relationshipWithOwner =
    RelationshipWithOwner.find((item) => item.code.toLowerCase() === props?.person.relationship_with_owner.toLowerCase())?.hdb_description ?? '-';
  const citizenship =
    ResidentialStatus.find((item) => item.code.toLowerCase() === props?.person?.myinfo?.residential_status.toLowerCase())?.description ?? '-';

  let childrenCareAndControlText = '';
  if (props.isOwner) {
    childrenCareAndControlText = ownerChildrenCareItems.find((item) => item.value === props.person.children_care_and_control)?.title ?? '';
  }

  const handleToggle = useCallback(() => {
    setIsOpen((value) => !value);
  }, [isOpen]);
  const personSex = Object.keys(SexEnum)
    .find((key) => SexEnum[key as keyof typeof SexEnum] === props.person.myinfo.sex)
    ?.toString();
  const loginUser = useAppSelector((state) => state.auth.AuthenticationSlice.loginUser);

  const spouseIDTypeTitle = props.person?.spouse_id_type
    ? idTypesInterface.find((item) => item.value === props.person.spouse_id_type)?.title ?? '-'
    : '';

  return (
    <P2Accordion
      defaultOpen={isOpen}
      onToggle={handleToggle}
      title={
        <div>
          <P2TextDescription color={isOpen ? '#333333' : '#0C8188'} size={isOpen ? '22px' : '20px'} title={props.person?.myinfo?.name} />
          {isOpen && (
            <p className='polymer-owner-review-uinfin'>{Helpers.maskPartialContent(props.person.myinfo?.uinfin, props?.enableMasking ?? false)}</p>
          )}
        </div>
      }>
      <div className='polymer-owner-review-accordion'>
        <div className='polymer-owner-review-accordion__wrapper'>
          {props.person.myinfo.uinfin !== loginUser.uinfin && childrenCareAndControlText !== '' && (
            <TextInfo variant='column' title='Does this owner have care and control of his/her children?' description={childrenCareAndControlText} />
          )}
          {props.isOwner ? (
            <OwnerText
              is_first_person={is_first_person}
              first_person={first_person}
              is_related={props.person.is_related ?? false}
              relationshipWithOwner={relationshipWithOwner}
            />
          ) : (
            <OccupierText first_person={first_person} relationshipWithOwner={relationshipWithOwner} />
          )}
          <div className='polymer-owner-review-accordion__fields-container'>
            <P2TextLabel bold={700} label={'Personal Details'} />
            <TextInfo
              variant='column'
              title='NRIC'
              description={Helpers.maskPartialContent(props.person.myinfo?.uinfin, props?.enableMasking ?? false)}
            />
            <TextInfo variant='column' title='Full name' description={props.person.myinfo?.name ? props.person.myinfo?.name : '-'} />
            {props.person.myinfo.uinfin === loginUser.uinfin && (
              <>
                {personSex && <TextInfo variant='column' title='Sex' description={personSex.charAt(0).toUpperCase() + personSex.slice(1)} />}
                {raceDropdownItems.filter((item) => item.value === props.person.myinfo.race).length > 0 && (
                  <TextInfo
                    variant='column'
                    title='Race'
                    description={raceDropdownItems.filter((item) => item.value === props.person.myinfo.race)[0].title}
                  />
                )}
                {NationalityCode.filter((item) => item.code === props.person.myinfo.nationality).length > 0 && (
                  <TextInfo
                    variant='column'
                    title='Nationality'
                    description={NationalityCode.filter((item) => item.code === props.person.myinfo.nationality)[0].description}
                  />
                )}
              </>
            )}
            {/* Residential status */}
            <TextInfo variant='column' title='Residential status' description={citizenship} />

            {/* Date of birth */}
            <TextInfo variant='column' title='Date of birth' description={props.person.myinfo?.dob ? props.person.myinfo?.dob : '-'} />

            {/* Marital Status */}
            <TextInfo
              variant='column'
              title='Marital Status'
              description={MaritalStatusCode.find((e) => e.code === props.person.myinfo?.marital_status)?.description ?? '-'}
            />

            {/* Identification type of spouse */}
            {spouseIDTypeTitle && <TextInfo variant='column' title='Identification type of spouse' description={spouseIDTypeTitle} />}

            {/* Identity number of spouse */}
            {spouseIDTypeTitle && (
              <TextInfo
                variant='column'
                title='Identity number of spouse'
                description={props.person?.spouse != '' ? Helpers.maskPartialContent(props.person?.spouse, props?.enableMasking ?? false) : '-'}
              />
            )}

            {/* Gross monthly income */}
            {showMonthlyIncome && props.isOwner && props.person.myinfo.uinfin === loginUser.uinfin && props.person.gross_monthly_income && (
              <TextInfo variant='column' title='Gross monthly income' description={props.person.gross_monthly_income} />
            )}

            {/* Date of SPR Obtained */}
            {props.isOwner && props.person.myinfo.residential_status === 'P' && (
              <TextInfo variant='column' title='Date of SPR Obtained' description={props.person.spr_obtained_date} />
            )}

            {/* Does this owner have care and control of his/her children? */}
            {props.person.myinfo.uinfin === loginUser.uinfin && childrenCareAndControlText !== '' && (
              <TextInfo
                variant='column'
                title='Does this owner have care and control of his/her children?'
                description={childrenCareAndControlText}
              />
            )}
          </div>
        </div>
      </div>
    </P2Accordion>
  );
}

export default OwnerOrOccupierReviewAccordion;
